// Thin axios wrapper around the tumbler scheduling endpoints listed in IMPLEMENTATION_PLAN.md's
// "API mapping" table. Every call takes an AbortController signal so callers can cancel stale
// requests on scope change (ASSESSMENT.md P2: "setScenarios ถูกเรียกก่อนตรวจ active ใน effect").
//
// This module does not decide success/failure semantics beyond HTTP — callers must read the
// returned resource's own status field (e.g. scenario.status) rather than trusting HTTP 200 alone.

import axios from 'axios';
import { apiUrl } from '../../../../constants';

function authHeaders(accessToken) {
  return { Authorization: `Bearer ${accessToken}` };
}

function unwrapList(data) {
  if (Array.isArray(data)) return { results: data, count: data.length, next: null, previous: null };
  return {
    results: data?.results || [],
    count: data?.count ?? (data?.results || []).length,
    next: data?.next || null,
    previous: data?.previous || null,
  };
}

// Operational tables must include every page, without sending credentials to a foreign next URL.
async function getAllPages(url, options) {
  const first = new URL(url, typeof window === 'undefined' ? 'http://localhost' : window.location.origin);
  let current = first;
  const results = [];
  const visited = new Set();
  while (current) {
    if (current.origin !== first.origin || visited.has(current.href)) throw new Error('Invalid API pagination URL');
    visited.add(current.href);
    const response = await axios.get(current.href, options);
    const page = unwrapList(response.data);
    results.push(...page.results);
    current = page.next ? new URL(page.next, current) : null;
  }
  return { data: results };
}

// ── Scheduler config ────────────────────────────────────────────────────────
export function getSchedulerConfig({ plant, factory, accessToken, signal }) {
  return axios.get(
    `${apiUrl.TUMBLER_SCHEDULER_CONFIG}?plant_id=${encodeURIComponent(plant)}&factory=${encodeURIComponent(factory)}`,
    { headers: authHeaders(accessToken), signal },
  );
}

export function saveSchedulerConfig({ configId, payload, accessToken }) {
  return configId
    ? axios.patch(`${apiUrl.TUMBLER_SCHEDULER_CONFIG}${configId}/`, payload, { headers: authHeaders(accessToken) })
    : axios.post(apiUrl.TUMBLER_SCHEDULER_CONFIG, payload, { headers: authHeaders(accessToken) });
}

// ── Scenarios ───────────────────────────────────────────────────────────────
export async function listScenarios({ plant, factory, page, accessToken, signal }) {
  const query = new URLSearchParams({ plant_id: plant, factory });
  if (page) query.set('page', String(page));
  const response = await axios.get(`${apiUrl.TUMBLER_SCHEDULE_SCENARIOS}?${query.toString()}`, {
    headers: authHeaders(accessToken),
    signal,
  });
  return unwrapList(response.data);
}

export function getScenarioDetail({ scenarioId, accessToken, signal }) {
  return axios.get(`${apiUrl.TUMBLER_SCHEDULE_SCENARIOS}${scenarioId}/`, {
    headers: authHeaders(accessToken),
    signal,
  });
}

export function createScenarioFromSources({ plant, factory, planningStart, planningEnd, useExistingStock, accessToken }) {
  return axios.post(`${apiUrl.TUMBLER_SCHEDULE_SCENARIOS}from-sources/`, {
    plant_id: plant,
    factory,
    planning_start: planningStart,
    planning_end: planningEnd,
    include_existing_cold_room_stock: useExistingStock,
  }, { headers: authHeaders(accessToken) });
}

export function runScenarioAction({ scenarioId, action, body = {}, accessToken }) {
  return axios.post(`${apiUrl.TUMBLER_SCHEDULE_SCENARIOS}${scenarioId}/${action}/`, body, {
    headers: authHeaders(accessToken),
  });
}

export function solveScenario(args) { return runScenarioAction({ ...args, action: 'solve' }); }
export function approveScenario(args) { return runScenarioAction({ ...args, action: 'approve' }); }
export function publishScenario(args) { return runScenarioAction({ ...args, action: 'publish' }); }
export function cancelScenario(args) { return runScenarioAction({ ...args, action: 'cancel' }); }
export function reconcileScenario(args) { return runScenarioAction({ ...args, action: 'reconcile' }); }

// reoptimize() must only be called on a PUBLISHED scenario id. The response already contains a
// solved revision — IMPLEMENTATION_PLAN.md Phase 3: "ห้ามเรียก solve ซ้ำเพราะ endpoint นี้ solve มาแล้ว".
export function reoptimizeScenario({ scenarioId, freezeAt, planningEnd, accessToken }) {
  return runScenarioAction({
    scenarioId,
    action: 'reoptimize',
    body: { freeze_at: freezeAt, planning_end: planningEnd },
    accessToken,
  });
}

// ── Batches ─────────────────────────────────────────────────────────────────
export function getBatchDetail({ batchId, accessToken, signal }) {
  return axios.get(`${apiUrl.TUMBLER_SCHEDULE_BATCHES}${batchId}/`, { headers: authHeaders(accessToken), signal });
}

export function runBatchAction({ batchId, action, body = {}, accessToken }) {
  return axios.post(`${apiUrl.TUMBLER_SCHEDULE_BATCHES}${batchId}/${action}/`, body, {
    headers: authHeaders(accessToken),
  });
}

export function startBatch(args) { return runBatchAction({ ...args, action: 'start' }); }
export function completeBatch(args) { return runBatchAction({ ...args, action: 'complete' }); }
export function transferBatchToColdRoom(args) { return runBatchAction({ ...args, action: 'transfer-to-cold-room' }); }
export function withdrawBatchFromColdRoom(args) { return runBatchAction({ ...args, action: 'withdraw-from-cold-room' }); }
export function rescheduleBatch(args) { return runBatchAction({ ...args, action: 'reschedule' }); }

// ── Product / allergen master ───────────────────────────────────────────────
export function listAllergens({ accessToken, signal }) {
  return getAllPages(apiUrl.TUMBLER_ALLERGEN_MASTER, { headers: authHeaders(accessToken), signal });
}

export function createAllergen({ payload, accessToken }) {
  return axios.post(apiUrl.TUMBLER_ALLERGEN_MASTER, payload, { headers: authHeaders(accessToken) });
}

export function listProductMaster({ plant, factory, accessToken, signal }) {
  return getAllPages(
    `${apiUrl.TUMBLER_PRODUCT_MASTER}?plant_id=${encodeURIComponent(plant)}&factory=${encodeURIComponent(factory)}`,
    { headers: authHeaders(accessToken), signal },
  );
}

export function saveProductMaster({ productId, payload, accessToken }) {
  return productId
    ? axios.patch(`${apiUrl.TUMBLER_PRODUCT_MASTER}${productId}/`, payload, { headers: authHeaders(accessToken) })
    : axios.post(apiUrl.TUMBLER_PRODUCT_MASTER, payload, { headers: authHeaders(accessToken) });
}

export function deleteProductMaster({ productId, accessToken }) {
  return axios.delete(`${apiUrl.TUMBLER_PRODUCT_MASTER}${productId}/`, { headers: authHeaders(accessToken) });
}

// ── Pots / cleaning rules (Phase 5 resources) ───────────────────────────────
export function listPots({ plant, factory, accessToken, signal }) {
  return getAllPages(
    `${apiUrl.TUMBLER_MASTER_POTS}?plant_id=${encodeURIComponent(plant)}&factory=${encodeURIComponent(factory)}`,
    { headers: authHeaders(accessToken), signal },
  );
}

export function savePot({ potId, payload, accessToken }) {
  return potId
    ? axios.patch(`${apiUrl.TUMBLER_MASTER_POTS}${potId}/`, payload, { headers: authHeaders(accessToken) })
    : axios.post(apiUrl.TUMBLER_MASTER_POTS, payload, { headers: authHeaders(accessToken) });
}

export function listCleaningRules({ plant, factory, accessToken, signal }) {
  return getAllPages(
    `${apiUrl.TUMBLER_CLEANING_RULES}?plant_id=${encodeURIComponent(plant)}&factory=${encodeURIComponent(factory)}`,
    { headers: authHeaders(accessToken), signal },
  );
}

export function saveCleaningRule({ ruleId, payload, accessToken }) {
  return ruleId
    ? axios.patch(`${apiUrl.TUMBLER_CLEANING_RULES}${ruleId}/`, payload, { headers: authHeaders(accessToken) })
    : axios.post(apiUrl.TUMBLER_CLEANING_RULES, payload, { headers: authHeaders(accessToken) });
}

// ── Operational reads ────────────────────────────────────────────────────────
export function listSetupActivities({ scenarioId, accessToken, signal }) {
  return getAllPages(`${apiUrl.TUMBLER_SETUP_ACTIVITIES}?scenario=${encodeURIComponent(scenarioId)}`, {
    headers: authHeaders(accessToken), signal,
  });
}

export function listColdRoomReservations({ plant, factory, accessToken, signal }) {
  return getAllPages(
    `${apiUrl.TUMBLER_COLD_ROOM_RESERVATIONS}?plant_id=${encodeURIComponent(plant)}&factory=${encodeURIComponent(factory)}`,
    { headers: authHeaders(accessToken), signal },
  );
}

export function listScheduleViolations({ scenarioId, accessToken, signal }) {
  return getAllPages(`${apiUrl.TUMBLER_SCHEDULE_VIOLATIONS}?scenario=${encodeURIComponent(scenarioId)}`, {
    headers: authHeaders(accessToken), signal,
  });
}

export function listScheduleAuditLogs({ plant, factory, accessToken, signal }) {
  return getAllPages(
    `${apiUrl.TUMBLER_SCHEDULE_AUDIT_LOGS}?plant_id=${encodeURIComponent(plant)}&factory=${encodeURIComponent(factory)}`,
    { headers: authHeaders(accessToken), signal },
  );
}

export function listBarrelAllocations({ scenarioId, plant, factory, accessToken, signal }) {
  const query = new URLSearchParams(scenarioId ? { scenario: scenarioId } : { plant_id: plant, factory, status: 'RESERVED' });
  return getAllPages(`${apiUrl.TUMBLER_BARREL_ALLOCATIONS}?${query}`, {
    headers: authHeaders(accessToken), signal,
  });
}

export function planBarrelDispatch({ allocationId, plant, factory, accessToken, body }) {
  const query = new URLSearchParams({ plant_id: plant, factory });
  return axios.post(`${apiUrl.TUMBLER_BARREL_ALLOCATIONS}${allocationId}/plan-dispatch/?${query}`, body, { headers: authHeaders(accessToken) });
}

export function listBarrelDispatches({ plant, factory, accessToken, signal }) {
  return getAllPages(
    `${apiUrl.TUMBLER_BARREL_DISPATCHES}?plant_id=${encodeURIComponent(plant)}&factory=${encodeURIComponent(factory)}`,
    { headers: authHeaders(accessToken), signal },
  );
}

export function runDispatchAction({ dispatchId, action, body = {}, plant, factory, accessToken }) {
  return axios.post(`${apiUrl.TUMBLER_BARREL_DISPATCHES}${dispatchId}/${action}/?${new URLSearchParams({ plant_id: plant, factory })}`, body, {
    headers: authHeaders(accessToken),
  });
}

export function startDispatch(args) { return runDispatchAction({ ...args, action: 'start' }); }
export function deliverDispatch(args) { return runDispatchAction({ ...args, action: 'deliver' }); }
export function cancelDispatch(args) { return runDispatchAction({ ...args, action: 'cancel' }); }
